import React from 'react';
import iconsData from '../data/icons.json';
import { PlatformFallbackIcons } from './icons';

// Create a mapping object for faster lookups
const createIconMapping = () => {
    const mapping = {};
    iconsData.forEach(item => {
        // Create multiple keys for better matching
        const normalizedName = item.icon_name.toLowerCase().replace(/\s+/g, '');
        mapping[normalizedName] = item.icon_url;

        // Additional mappings for common platforms
        // Direct platform mappings
        if (item.icon_name.toLowerCase() === 'google') {
            mapping['google'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'facebook') {
            mapping['facebook'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'yelp') {
            mapping['yelp'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'tripadvisor') {
            mapping['tripadvisor'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'airbnb') {
            mapping['airbnb'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'booking') {
            mapping['booking'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase() === 'amazon') {
            mapping['amazon'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('app store')) {
            mapping['apple-app-store'] = item.icon_url;
            mapping['appstore'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('bbb')) {
            mapping['bbb'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('capterra')) {
            mapping['capterra'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('dealer')) {
            mapping['dealerrater'] = item.icon_url;
            mapping['dealer-rater'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('ebay')) {
            mapping['ebay'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('edmunds')) {
            mapping['edmunds'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('g2')) {
            mapping['g2'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('google play')) {
            mapping['google-play'] = item.icon_url;
            mapping['googleplay'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('hotels')) {
            mapping['hotels'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('houzz')) {
            mapping['houzz'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('linkedin')) {
            mapping['linkedin'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('opentable') || item.icon_name.toLowerCase().includes('open table')) {
            mapping['opentable'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('trustpilot')) {
            mapping['trustpilot'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('sitejabber')) {
            mapping['sitejabber'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('foursquare')) {
            mapping['foursquare'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('glassdoor')) {
            mapping['glassdoor'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('indeed')) {
            mapping['indeed'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('zillow')) {
            mapping['zillow'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('health') && item.icon_name.toLowerCase().includes('grades')) {
            mapping['healthgrades'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('vitals')) {
            mapping['vitals'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('zoc') && item.icon_name.toLowerCase().includes('doc')) {
            mapping['zocdoc'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('grub') && item.icon_name.toLowerCase().includes('hub')) {
            mapping['grubhub'] = item.icon_url;
        }
        if (item.icon_name.toLowerCase().includes('doordash')) {
            mapping['doordash'] = item.icon_url;
        }
    });

    return mapping;
};

const iconMapping = createIconMapping();

// Function to get platform icon URL
export const getPlatformIconUrl = (platformId) => {
    if (!platformId || typeof platformId !== 'string') return null;

    const normalizedId = platformId.toLowerCase().replace(/[-_\s]/g, '');
    return iconMapping[normalizedId] || null;
};

// Function to get platform icon component
export const PlatformIcon = ({ platform, className = "w-6 h-6", alt = "" }) => {
    // Ensure platform is a string
    const platformStr = typeof platform === 'string' ? platform : String(platform || 'unknown');
    const iconUrl = getPlatformIconUrl(platformStr);

    if (!iconUrl) {
        // Fallback to Material Icons for platforms not found
        const FallbackIconComponent = PlatformFallbackIcons[platformStr] || PlatformFallbackIcons['all'];
        return (
            <FallbackIconComponent
                className={className}
                style={{ fontSize: '1.5rem' }}
            />
        );
    }

    return (
        <img
            src={iconUrl}
            alt={alt || `${platformStr} icon`}
            className={className}
            onError={(e) => {
                // Fallback if image fails to load - replace with Material Icon
                e.target.style.display = 'none';
                const fallbackContainer = document.createElement('div');
                const FallbackIconComponent = PlatformFallbackIcons[platformStr] || PlatformFallbackIcons['all'];

                // Create a React element and render it
                import('react-dom/client').then(({ createRoot }) => {
                    const root = createRoot(fallbackContainer);
                    root.render(React.createElement(FallbackIconComponent, {
                        style: { fontSize: '1.5rem' }
                    }));
                });

                e.target.parentNode.appendChild(fallbackContainer);
            }}
        />
    );
};

// Function to get all available platforms with their icons
export const getAllPlatformsWithIcons = () => {
    return iconsData.map(item => ({
        id: item.icon_name.toLowerCase().replace(/\s+/g, '-'),
        name: item.icon_name,
        icon_url: item.icon_url,
        normalizedId: item.icon_name.toLowerCase().replace(/[-_\s]/g, '')
    }));
};

// Function to get enhanced platform data with proper icons
export const getPlatformsWithIcons = () => {
    return [
        {
            id: 'google',
            name: 'Google Reviews',
            color: 'bg-blue-500',
            iconUrl: getPlatformIconUrl('google') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/google.svg'
        },
        {
            id: 'facebook',
            name: 'Facebook Reviews',
            color: 'bg-blue-600',
            iconUrl: getPlatformIconUrl('facebook') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/facebook.svg'
        },
        {
            id: 'yelp',
            name: 'Yelp Reviews',
            color: 'bg-red-600',
            iconUrl: getPlatformIconUrl('yelp') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/yelp.svg'
        },
        {
            id: 'tripadvisor',
            name: 'TripAdvisor Reviews',
            color: 'bg-green-600',
            iconUrl: getPlatformIconUrl('tripadvisor') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/tripadvisor.svg'
        },
        {
            id: 'airbnb',
            name: 'Airbnb Reviews',
            color: 'bg-red-500',
            iconUrl: getPlatformIconUrl('airbnb') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/airbnb.svg'
        },
        {
            id: 'booking',
            name: 'Booking.com Reviews',
            color: 'bg-blue-700',
            iconUrl: getPlatformIconUrl('booking') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/booking.svg'
        },
        {
            id: 'amazon',
            name: 'Amazon Reviews',
            color: 'bg-orange-500',
            iconUrl: getPlatformIconUrl('amazon') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/amazon.svg'
        },
        {
            id: 'apple-app-store',
            name: 'Apple App Store Reviews',
            color: 'bg-gray-800',
            iconUrl: getPlatformIconUrl('apple-app-store') || 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/appstore.svg'
        },
        { id: 'google-play', name: 'Google Play Reviews', color: 'bg-green-500' },
        { id: 'g2', name: 'G2 Reviews', color: 'bg-purple-600' },
        { id: 'capterra', name: 'Capterra Reviews', color: 'bg-orange-600' },
        { id: 'houzz', name: 'Houzz Reviews', color: 'bg-green-700' },
        { id: 'bbb', name: 'BBB Reviews', color: 'bg-blue-600' },
        { id: 'dealerrater', name: 'DealerRater Reviews', color: 'bg-green-600' },
        { id: 'ebay', name: 'eBay Reviews', color: 'bg-yellow-500' },
        { id: 'edmunds', name: 'Edmunds Reviews', color: 'bg-blue-800' },
        { id: 'hotels', name: 'Hotels.com Reviews', color: 'bg-red-600' },
        { id: 'linkedin', name: 'LinkedIn Reviews', color: 'bg-blue-700' },
        { id: 'opentable', name: 'OpenTable Reviews', color: 'bg-red-700' },
        { id: 'trustpilot', name: 'Trustpilot Reviews', color: 'bg-green-500' },
        { id: 'sitejabber', name: 'Sitejabber Reviews', color: 'bg-purple-600' },
        { id: 'foursquare', name: 'Foursquare Reviews', color: 'bg-pink-600' },
        { id: 'glassdoor', name: 'Glassdoor Reviews', color: 'bg-green-600' },
        { id: 'indeed', name: 'Indeed Reviews', color: 'bg-blue-700' },
        { id: 'zillow', name: 'Zillow Reviews', color: 'bg-blue-600' },
        { id: 'healthgrades', name: 'Healthgrades Reviews', color: 'bg-green-500' },
        { id: 'vitals', name: 'Vitals Reviews', color: 'bg-blue-500' },
        { id: 'zocdoc', name: 'ZocDoc Reviews', color: 'bg-yellow-500' },
        { id: 'grubhub', name: 'Grubhub Reviews', color: 'bg-red-600' },
        { id: 'doordash', name: 'DoorDash Reviews', color: 'bg-red-500' }
    ].map(platform => ({
        ...platform,
        iconUrl: platform.iconUrl || getPlatformIconUrl(platform.id) || ''
    }));
};

// Legacy export for backwards compatibility
export const platformsWithIcons = getPlatformsWithIcons(); 